import React from 'react';
import { ApolloProvider } from '@apollo/client';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { ThemeProvider as MuiThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { apolloClient } from './graphql/client';
import { AuthProvider } from './contexts/AuthContext';
import { ThemeProvider, useTheme } from './contexts/ThemeContext';
import { ProtectedRoute } from './components/ProtectedRoute';
import { AppLayout } from './components/layout/AppLayout';
import DiseaseList from './components/DiseaseList';
import DiseaseEdit from './components/DiseaseEdit';
import TreatmentList from './components/TreatmentList';
import TreatmentEdit from './components/TreatmentEdit';
import TrainingSampleList from './components/TrainingSampleList';
import TrainingSampleDetail from './components/TrainingSampleDetail';
import TrainingSampleExport from './components/TrainingSampleExport';
import Dashboard from './components/Dashboard';
import { createAppTheme } from './theme/theme';

// AppWithTheme component uses the theme context to create the appropriate MUI theme
const AppWithTheme: React.FC = () => {
  const { resolvedMode } = useTheme();
  const theme = createAppTheme(resolvedMode);
  
  return (
    <MuiThemeProvider theme={theme}>
      <CssBaseline />
      <Router>
        <ProtectedRoute>
          <AppLayout>
            <Routes>
              <Route path="/" element={<Dashboard />} />
              <Route path="/diseases" element={<DiseaseList />} />
              <Route path="/diseases/new" element={<DiseaseEdit />} />
              <Route path="/diseases/:id" element={<DiseaseEdit />} />
              <Route path="/treatments" element={<TreatmentList />} />
              <Route path="/treatments/new" element={<TreatmentEdit />} />
              <Route path="/treatments/:id" element={<TreatmentEdit />} />
              <Route path="/training-samples" element={<TrainingSampleList />} />
              <Route path="/training-samples/:id" element={<TrainingSampleDetail />} />
              <Route path="/training-export" element={<TrainingSampleExport />} />
            </Routes>
          </AppLayout>
        </ProtectedRoute>
      </Router>
    </MuiThemeProvider>
  );
};

// Main App component wraps everything with providers
function App() {
  return (
    <AuthProvider>
      <ApolloProvider client={apolloClient}>
        <ThemeProvider>
          <AppWithTheme />
        </ThemeProvider>
      </ApolloProvider>
    </AuthProvider>
  );
}

export default App;
